/*
 * Shopify port of the Codrops Reflection Scroll architecture, Demo 2.
 *
 * The DOM intentionally keeps the same structural classes as the reference:
 * .content / .content__title / .item / .item__img / .item__img-inner /
 * .item__caption / .wrap / .content--reflection.
 *
 * Original reference:
 * https://github.com/codrops/ReflectionScroll
 * https://tympanus.net/Development/ReflectionScroll/index2.html
 */

(() => {
  if (window.__GWEReflectionScrollCodropsLoaded) return;
  window.__GWEReflectionScrollCodropsLoaded = true;

  const instances = new Map();

  class ReflectionScrollShopify {
    constructor(root) {
      this.root = root;
      this.source = root.querySelector('[data-reflection-source]');
      this.wrap = root.querySelector('[data-reflection-wrap]');
      this.scene = root.querySelector('[data-reflection-scene]');
      this.copy = root.querySelector('[data-reflection-copy]');

      this.sourceImages = Array.from(
        root.querySelectorAll(
          '[data-reflection-source] [data-reflection-image]'
        )
      );

      this.reflectionOpacity = parseFloat(
        root.dataset.reflectionOpacity || '1'
      );
      this.distortion = parseFloat(root.dataset.distortion || '10');
      this.blurAmount = parseFloat(root.dataset.blur || '5');
      this.imageShift = parseFloat(root.dataset.imageShift || '16');

      this.scroll = window.scrollY;
      this.lastScroll = window.scrollY;
      this.velocity = 0;
      this.smoothVelocity = 0;
      this.frame = null;
      this.resizeObserver = null;
      this.cleanScroll = null;

      this.init();
    }

    init() {
      if (!this.source || !this.wrap || !this.scene || !this.copy) {
        return;
      }

      this.prepareReflection();

      this.waitForImages(() => {
        this.measure();
        this.bindResize();
        this.bindScroll();
        this.render(true);

        this.root.classList.add('is-ready');

        requestAnimationFrame(() => {
          this.measure();
          this.render(true);
        });
      });
    }

    waitForImages(done) {
      if (window.imagesLoaded) {
        window.imagesLoaded(
          this.root,
          {
            background: '[data-reflection-image]'
          },
          done
        );

        return;
      }

      const urls = Array.from(
        this.root.querySelectorAll('[data-reflection-image]')
      )
        .map((element) => {
          const value = getComputedStyle(element).backgroundImage;
          const match = value.match(/url\(["']?(.*?)["']?\)/);
          return match?.[1] || null;
        })
        .filter(Boolean);

      if (!urls.length) {
        done();
        return;
      }

      Promise.all(
        urls.map((url) => new Promise((resolve) => {
          const image = new Image();
          image.onload = resolve;
          image.onerror = resolve;
          image.src = url;
        }))
      ).then(done);
    }

    prepareReflection() {
      this.wrap.querySelectorAll('a').forEach((link) => {
        link.removeAttribute('href');
        link.setAttribute('tabindex', '-1');
        link.setAttribute('aria-hidden', 'true');
      });
    }

    measure() {
      const sourceRect = this.source.getBoundingClientRect();
      const wrapRect = this.wrap.getBoundingClientRect();

      this.sourceTop = window.scrollY + sourceRect.top;
      this.reflectionHeight = wrapRect.height;
    }

    bindResize() {
      this.resizeObserver = new ResizeObserver(() => {
        this.measure();
        this.render(true);
      });

      this.resizeObserver.observe(this.root);
      this.resizeObserver.observe(this.source);
      this.resizeObserver.observe(this.wrap);

      this.onResize = () => {
        this.measure();
        this.render(true);
      };

      window.addEventListener('resize', this.onResize, {
        passive: true
      });
    }

    bindScroll() {
      const lenis = window.GWEMotion?.lenis;

      if (lenis?.on) {
        const onLenisScroll = (event) => {
          const nextScroll = Number.isFinite(event?.animatedScroll)
            ? event.animatedScroll
            : window.scrollY;

          const nextVelocity = Number.isFinite(event?.velocity)
            ? event.velocity
            : nextScroll - this.lastScroll;

          this.lastScroll = nextScroll;
          this.scroll = nextScroll;
          this.velocity = nextVelocity;

          this.requestRender();
        };

        lenis.on('scroll', onLenisScroll);

        this.cleanScroll = () => {
          lenis.off?.('scroll', onLenisScroll);
        };

        return;
      }

      const onNativeScroll = () => {
        const nextScroll = window.scrollY;

        this.velocity = nextScroll - this.lastScroll;
        this.lastScroll = nextScroll;
        this.scroll = nextScroll;

        this.requestRender();
      };

      window.addEventListener('scroll', onNativeScroll, {
        passive: true
      });

      this.cleanScroll = () => {
        window.removeEventListener('scroll', onNativeScroll);
      };
    }

    requestRender() {
      if (this.frame) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.render();
      });
    }

    render(force = false) {
      const scroll = Number.isFinite(this.scroll)
        ? this.scroll
        : window.scrollY;

      /*
       * The clone is mirrored around the lower edge of the fixed reflection
       * window. This is the same geometric principle used by the reference.
       */
      const mirroredY =
        2 * this.reflectionHeight -
        this.sourceTop +
        scroll;

      this.copy.style.transform =
        `translate3d(0, ${mirroredY.toFixed(2)}px, 0) scaleY(-1)`;

      /*
       * Demo 2 adds velocity-driven filtering and 3D movement.
       */
      this.smoothVelocity +=
        (this.velocity - this.smoothVelocity) * 0.14;

      const direction = Math.max(
        -1,
        Math.min(1, this.smoothVelocity / 18)
      );

      const speed = Math.abs(direction);

      const rotateX =
        direction * this.distortion * 0.55;

      const translateZ =
        speed * this.distortion * 3;

      const stretch =
        1 + speed * this.distortion * 0.004;

      const blur =
        speed * this.blurAmount;

      const brightness =
        1 - speed * 0.24;

      const saturate =
        1 - speed * 0.28;

      this.scene.style.transform =
        `perspective(900px) rotateX(${rotateX.toFixed(2)}deg) translateZ(${translateZ.toFixed(2)}px) scaleY(${stretch.toFixed(4)})`;

      this.scene.style.filter =
        `blur(${blur.toFixed(2)}px) brightness(${brightness.toFixed(3)}) saturate(${saturate.toFixed(3)})`;

      this.scene.style.opacity =
        String(this.reflectionOpacity);

      /*
       * Small internal image translation gives the real stream the same
       * scroll-reactive feel as the reference Demo 2.
       */
      const imageY =
        direction * -this.imageShift;

      const imageScale =
        1.01 + speed * 0.025;

      this.sourceImages.forEach((image) => {
        image.style.transform =
          `translate3d(0, ${imageY.toFixed(2)}px, 0) scale(${imageScale.toFixed(4)})`;

        image.style.filter =
          speed > 0.01
            ? `contrast(${(1 + speed * 0.08).toFixed(3)})`
            : 'none';
      });

      const rootRect = this.root.getBoundingClientRect();
      const isVisible =
        rootRect.bottom > 0 &&
        rootRect.top < window.innerHeight;

      this.wrap.style.visibility =
        isVisible ? 'visible' : 'hidden';

      if (!force && Math.abs(this.smoothVelocity) > 0.018) {
        this.velocity *= 0.82;
        this.requestRender();
      } else if (!force) {
        this.velocity = 0;
        this.smoothVelocity = 0;

        this.scene.style.transform =
          'perspective(900px) rotateX(0deg) translateZ(0px) scaleY(1)';

        this.scene.style.filter =
          'blur(0px) brightness(1) saturate(1)';

        this.sourceImages.forEach((image) => {
          image.style.transform =
            'translate3d(0,0,0) scale(1.01)';
          image.style.filter = 'none';
        });
      }
    }

    destroy() {
      if (this.frame) {
        cancelAnimationFrame(this.frame);
      }

      this.cleanScroll?.();
      this.resizeObserver?.disconnect();

      if (this.onResize) {
        window.removeEventListener('resize', this.onResize);
      }

      instances.delete(this.root);
    }
  }

  const initRoot = (root) => {
    if (!root || instances.has(root)) return;

    instances.set(
      root,
      new ReflectionScrollShopify(root)
    );
  };

  const initAll = (scope = document) => {
    scope
      .querySelectorAll?.('[data-reflection-scroll]')
      .forEach(initRoot);

    if (scope.matches?.('[data-reflection-scroll]')) {
      initRoot(scope);
    }
  };

  const boot = () => {
    initAll(document);

    document.addEventListener('shopify:section:load', (event) => {
      initAll(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const root =
        event.target.matches?.('[data-reflection-scroll]')
          ? event.target
          : event.target.querySelector?.('[data-reflection-scroll]');

      if (root) {
        instances.get(root)?.destroy();
      }
    });

    document.addEventListener('shopify:section:reorder', () => {
      instances.forEach((instance) => {
        instance.measure();
        instance.render(true);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, {
      once: true
    });
  } else {
    boot();
  }
})();
